import {Injectable} from '@angular/core';
import {TkFile} from '../../interfaces/core/tk-file';
import {DeleteFileOptions, Directory, Filesystem} from '@capacitor/filesystem';
import {AlertController, Platform} from '@ionic/angular';
import {FilesystemService} from './filesystem.service';
import {FileOpener, FileOpenerOptions} from '@capacitor-community/file-opener';


@Injectable({
  providedIn: 'root'
})
export class DocumentsService {

  constructor(private platform: Platform,
              private alertController: AlertController,
              private filesystemService: FilesystemService) {
  }

  /**
   *
   * @param tkFile
   */
  public async readDocument(tkFile: TkFile): Promise<Blob> {
    try {
      if (this.platform.is('hybrid')) {
        const readFile = await Filesystem.readFile({
          path: tkFile.filepath
        });
        return this.filesystemService.base64ToBlob(readFile.data as string, this.getMimeType(tkFile));
      }
      else {
        const readFile = await Filesystem.readFile({
          path: tkFile.filepath,
          directory: Directory.Data
        });
        return this.filesystemService.base64ToBlob(readFile.data as string, this.getMimeType(tkFile));
      }
    }
    catch (error) {
      console.error('Fehler beim Lesen der Datei:', error);
      throw error;
    }
  }

  /**
   *
   * @param documentFile
   * @param filename
   */
  public downloadDocument(documentFile: Blob, filename: string): void {
    const link = document.createElement('a');
    const objectUrl = URL.createObjectURL(documentFile);
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  }

  /**
   *
   * @param tkFile
   * @param fileName
   */
  public async showDocument(tkFile: TkFile, fileName: string = this.getFileName(tkFile)): Promise<void> {
    if(this.platform.is('hybrid')) {
      const fileOpenerOptions: FileOpenerOptions = {
        filePath: tkFile.filepath,
        contentType: this.getMimeType(tkFile)
      };
      try {
        await FileOpener.open(fileOpenerOptions);
      }
      catch(e) {
        const alert = await this.alertController.create({
          header: 'Datei kann nicht geöffnet werden',
          message: 'Für diesen Dateityp ist keine passende App installiert.',
          buttons: ['OK']
        });
        await alert.present();
      }
    }
    else {
      const documentFile = await this.readDocument(tkFile);
      this.downloadDocument(documentFile, fileName);
    }
  }

  /**
   *
   * @param tkFile
   * @param data
   * @param path
   */
  public async saveDocument(
    tkFile: TkFile,
    data: any,
    path: string = null
  ): Promise<TkFile> {
    tkFile.format = tkFile.format || 'bin';
    const timestamp = new Date().getTime();
    tkFile.name = tkFile.name || `${timestamp}.${tkFile.format}`;
    const fileName = this.filesystemService.generatePath(path) + `${timestamp}.${tkFile.format}`;

    const file = await Filesystem.writeFile({
      path: fileName,
      data: data,
      directory: Directory.Data,
      recursive: true,
      // encoding: encoding
    });

    if(this.platform.is('hybrid')) {
      tkFile.filepath = file.uri;
    } else {
      tkFile.filepath = fileName;
    }

    tkFile.loaded = true;
    return tkFile;
  }

  /**
   *
   * @param tkFile
   */
  public async deleteDocument(tkFile: TkFile): Promise<void> {
    if (tkFile.filepath) {
      const deleteFileOptions: DeleteFileOptions = {path: tkFile.filepath};
      if (!this.platform.is('hybrid')) {
        deleteFileOptions.directory = Directory.Data;
      }
      await Filesystem.deleteFile(deleteFileOptions);
    }
  }

  private getMimeType(tkFile: TkFile): string {
    if(tkFile.mimeType) {
      return tkFile.mimeType;
    }
    if(tkFile.format?.toLowerCase() === 'pdf') {
      return 'application/pdf';
    }
    return 'application/octet-stream';
  }

  private getFileName(tkFile: TkFile): string {
    if(tkFile.name) {
      return tkFile.name;
    }
    const extension = tkFile.format || 'bin';
    return `${new Date().getTime()}.${extension}`;
  }
}
